import {
  Chart,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartData,
  ChartDatasetCustomTypesPerDataset,
} from "chart.js";
import { Box, Divider, Skeleton } from "@mui/material";
import { Devices } from "@mui/icons-material";
import { graphColors } from "shared-ui/src/theme/shiColors";
import { Line } from "react-chartjs-2";
import {
  MonthlySpendDataDto,
  GetPurchasedAssetsSpendWidgetRequest,
  PurchasedAssetsSpendWidgetClient,
} from "@lib/ShiOneClient";
import { NoContent } from "shared-ui";
import { useApi } from "@utils/useApi";
import { useChartJsDefaults } from "shared-ui/src/theme/useChartJsDefaults";
import { useQuery } from "@tanstack/react-query";
import { formatShortNumber } from "../AssetsSpendWidget";
import { DateTime } from "luxon";

function alignSeries(
  series: MonthlySpendDataDto[],
  reference: MonthlySpendDataDto[],
  yearOffset = 1,
): (number | null)[] {
  return reference.map((ref) => {
    const match = series.find(
      (d) =>
        (d.month ?? 0) === (ref.month ?? 0) &&
        (d.year ?? 0) === (ref.year ?? 0) - yearOffset,
    );
    return match ? (match.amount ?? null) : null;
  });
}

function getMonthLabel(month: number) {
  const monthLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return monthLabels[month - 1];
}

function sortByMonth(data: MonthlySpendDataDto[]): MonthlySpendDataDto[] {
  return [...data].sort((a, b) => {
    const aYear = a.year ?? 0;
    const bYear = b.year ?? 0;
    if (aYear !== bYear) return aYear - bYear;
    const aMonth = a.month ?? 0;
    const bMonth = b.month ?? 0;
    return aMonth - bMonth;
  });
}

const useGetSpendComparison = () => {
  const request = {
    clientLocalNow: DateTime.local().toISO(),
  } as unknown as GetPurchasedAssetsSpendWidgetRequest;
  const api = useApi(PurchasedAssetsSpendWidgetClient);
  return useQuery({
    queryKey: ["purchased-assets-spend-widget", "comparison"],
    queryFn: () => api.getSpendComparison(request),
  });
};

Chart.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

export default function AssetsSpendLineChart({
  termMonths,
}: Readonly<{ termMonths: number }>) {
  const { data: response, isLoading } = useGetSpendComparison();
  const monthlySpendData = response?.data?.monthlySpendData ?? [];
  useChartJsDefaults();
  const colorPalette = graphColors;
  const sortedSpendData = sortByMonth(monthlySpendData);

  function getLastN(n: number) {
    return sortedSpendData.slice(-n);
  }

  function getDatasets(termMonths: number) {
    if (termMonths === 6) {
      const months6 = getLastN(6);
      const paddedData = new Array(6)
        .fill(null)
        .concat(months6.map((d) => d.amount ?? null));
      const dataSeries = paddedData.slice(-12);
      return [
        {
          type: "line" as const,
          label: `Last 6 Months`,
          data: dataSeries,
          fill: false,
          borderColor: colorPalette[0],
          backgroundColor: colorPalette[0],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[0],
        },
      ];
    } else if (termMonths === 12) {
      const months12 = getLastN(12);
      return [
        {
          type: "line" as const,
          label: `Last 12 Months`,
          data: months12.map((d) => d.amount ?? null),
          fill: false,
          borderColor: colorPalette[0],
          backgroundColor: colorPalette[0],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[0],
        },
      ];
    } else if (termMonths === 24) {
      const last12 = getLastN(12);
      const months2412 = sortedSpendData.slice(-24, -12);
      return [
        {
          type: "line" as const,
          label: "Last 12 Months",
          data: last12.map((d) => d.amount ?? null),
          fill: false,
          borderColor: colorPalette[0],
          backgroundColor: colorPalette[0],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[0],
        },
        {
          type: "line" as const,
          label: "24-12",
          data: alignSeries(months2412, last12),
          fill: false,
          borderColor: colorPalette[1],
          backgroundColor: colorPalette[1],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[1],
        },
      ];
    } else if (termMonths === 36) {
      const last12 = getLastN(12);
      const months2412 = sortedSpendData.slice(-24, -12);
      const months3624 = sortedSpendData.slice(-36, -24);
      return [
        {
          type: "line" as const,
          label: "Last 12 Months",
          data: last12.map((d) => d.amount ?? null),
          fill: false,
          borderColor: colorPalette[0],
          backgroundColor: colorPalette[0],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[0],
        },
        {
          type: "line" as const,
          label: "24-12",
          data: alignSeries(months2412, last12, 1),
          fill: false,
          borderColor: colorPalette[1],
          backgroundColor: colorPalette[1],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[1],
        },
        {
          type: "line" as const,
          label: "36-24",
          data: alignSeries(months3624, last12, 2),
          fill: false,
          borderColor: colorPalette[2],
          backgroundColor: colorPalette[2],
          tension: 0,
          pointRadius: 4,
          pointBackgroundColor: colorPalette[2],
        },
      ];
    }
    return [];
  }

  const last12 = getLastN(12);
  const xLabels = last12.map((d) => getMonthLabel(d.month ?? 1));
  const datasets: ChartDatasetCustomTypesPerDataset<"line">[] =
    getDatasets(termMonths);
  const totalSpend = getLastN(termMonths).reduce(
    (sum, entry) => sum + (entry.amount ?? 0),
    0,
  );

  const chartData: ChartData<"line"> = {
    labels: xLabels,
    datasets: datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { display: true, position: "bottom" as const },
      tooltip: {
        enabled: true,
        callbacks: {
          title: (tooltipItems: any) => {
            const idx = tooltipItems[0].dataIndex;
            const datasetIndex = tooltipItems[0].datasetIndex;
            let entry;
            if (datasetIndex === 0) {
              entry = last12[idx];
            } else if (datasetIndex === 1) {
              const prev12 = sortedSpendData.slice(-24, -12);
              entry = prev12.find(
                (d) =>
                  d.month === last12[idx].month &&
                  (d.year ?? 0) === (last12[idx]?.year ?? 0) - 1,
              );
            } else if (datasetIndex === 2) {
              const prevPrev12 = sortedSpendData.slice(-36, -24);
              entry = prevPrev12.find(
                (d) =>
                  d.month === last12[idx].month &&
                  (d.year ?? 0) === (last12[idx]?.year ?? 0) - 2,
              );
            }
            if (entry) {
              return `${getMonthLabel(entry.month ?? 1)} ${entry.year ?? ""}`;
            }
            return tooltipItems[0].label;
          },
        },
      },
    },
    layout: {
      padding: {
        right: 20,
      },
    },
    scales: {
      x: { title: { display: true, text: "" } },
      y: {
        title: { display: true, text: "" },
        beginAtZero: true,
        ticks: {
          callback: (tickValue: string | number) => {
            const num =
              typeof tickValue === "number" ? tickValue : Number(tickValue);
            return `$${formatShortNumber(num)}`;
          },
        },
      },
    },
  };

  return (
    <>
      {isLoading && (
        <Box sx={{ width: "100%", pt: 2 }}>
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={300} />
          </Box>
        </Box>
      )}
      {!isLoading && (
        <>
          <Box
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box color="gray.DEFAULT">Total Spend</Box>
            <Box color="gray.DEFAULT" sx={{ fontSize: "20px" }}>
              $
              {totalSpend.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Box>
          </Box>
          <Divider />
          {!!chartData.datasets?.length && (
            <Box sx={{ minHeight: 350, height: 350, width: "100%", py: 2 }}>
              <Line data={chartData} options={options} />
            </Box>
          )}
          {!chartData.datasets?.length && (
            <NoContent
              containerHeight={150}
              header={"No Data Returned"}
              body={"There is no spend data to display at the moment."}
              actionText={""}
              icon={<Devices />}
            />
          )}
        </>
      )}
    </>
  );
}
